// Ref: https://github.com/johnpolacek/date-range-picker-for-shadcn/tree/main

import { CalendarIcon, CheckIcon } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '../button/button';
import { Calendar } from '../calendar/calendar';
import { DateInput } from '../date-input/date-input';
import { Popover, PopoverContent, PopoverTrigger } from '../popover/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select/select';

import type { DateRange } from '@/entities/query/index';
import { DATE_FORMATS } from '@/shared/utils/date/date-formats';
import { getPresetDateRange, PresetName, PRESETS } from '@/shared/utils/get-preset-date-range';
import { cn } from '@/shared/utils/index';
import dayjs from 'dayjs';

export type DateRangeState =
  | {
      dateRange: DateRange;
    }
  | {
      presetName: PresetName;
    };

export interface DateRangePickerProps {
  /** Current date range value */
  value: DateRangeState;
  /** Callback when date range changes */
  onChange: (params: DateRangeState) => void;
  /** Control popover open state */
  open?: boolean;
  /** Callback when popover open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Alignment of popover */
  align?: 'start' | 'center' | 'end';
  /** Option for locale */
  locale?: string;
  /** Display preset name or 'Custom' instead of date range in button */
  showDateRange?: boolean;
}

/** The DateRangePicker component allows a user to select a range of dates */
export const DateRangePicker: FC<DateRangePickerProps> & {
  filePath: string;
} = ({
  value,
  onChange,
  open: controlledOpen,
  onOpenChange,
  align = 'end',
  showDateRange = true,
}) => {
  // Use controlled open state if provided, otherwise manage internally
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  // Internal state for temporary date range while the popover is open.
  // Closed display should always follow the controlled prop value so stale draft
  // state from a previous editing session cannot resurface when the same value
  // key appears again later.
  const [tempRange, setTempRange] = useState<DateRangeState>(value);
  const visibleRange = isOpen ? tempRange : value;

  const handleOpenChange = (nextOpen: boolean): void => {
    if (nextOpen) {
      setTempRange(value);
    }
    setIsOpen(nextOpen);
  };

  const computedDateRange = useMemo(() => {
    if ('dateRange' in visibleRange) {
      return visibleRange.dateRange;
    }
    return getPresetDateRange(visibleRange.presetName);
  }, [visibleRange]);

  const computedPresetName = useMemo(() => {
    if ('presetName' in visibleRange) {
      return visibleRange.presetName;
    }
    return null;
  }, [visibleRange]);

  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false,
  );

  useEffect(() => {
    const handleResize = (): void => {
      setIsSmallScreen(window.innerWidth < 960);
    };

    window.addEventListener('resize', handleResize);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleTempRangeChange = (newRange: DateRange): void => {
    setTempRange({ dateRange: newRange });
  };

  const setPreset = (preset: PresetName): void => {
    setTempRange({ presetName: preset });
  };

  const PresetButton = ({
    preset,
    label,
    isSelected,
  }: {
    preset: PresetName;
    label: string;
    isSelected: boolean;
  }) => (
    <Button
      className={cn(isSelected && 'pointer-events-none')}
      variant="ghost"
      onClick={() => {
        setPreset(preset);
      }}
    >
      <>
        <span className={cn('pr-2 opacity-0', isSelected && 'opacity-70')}>
          <CheckIcon width={18} height={18} />
        </span>
        {label}
      </>
    </Button>
  );

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleUpdate = () => {
    onChange(tempRange);
    setIsOpen(false);
  };

  // Get the display text for the button
  const getButtonDisplayText = () => {
    if (computedPresetName) {
      const preset = PRESETS.find((p) => p.name === computedPresetName);
      return preset?.label || 'Custom';
    }

    return 'Custom';
  };

  const isSelectedPreset = useCallback(
    (preset: PresetName) => {
      if ('presetName' in tempRange) {
        return tempRange.presetName === preset;
      }
      return false;
    },
    [tempRange],
  );

  return (
    <div className="flex w-fit shrink-0 items-center gap-3">
      {showDateRange && computedDateRange && (
        <span className="text-muted-foreground hidden w-fit shrink-0 text-sm xl:block">
          {dayjs(computedDateRange.startDate).format(DATE_FORMATS.base)} -{' '}
          {dayjs(computedDateRange.endDate).format(DATE_FORMATS.base)}
        </span>
      )}
      <Popover modal={true} open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild className="justify-start">
          <Button className="cursor-pointer px-4 py-2" variant="outline">
            <CalendarIcon className="size-4" />
            {getButtonDisplayText()}
          </Button>
        </PopoverTrigger>
        <PopoverContent align={align} className="w-auto">
          <div className="flex py-2">
            <div className="flex">
              <div className="flex flex-col">
                <div className="flex flex-col items-center justify-end gap-2 px-3 pb-4 lg:flex-row lg:items-start lg:pb-0">
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <DateInput
                        value={computedDateRange.startDate}
                        onChange={(date) => {
                          handleTempRangeChange({
                            startDate: date,
                            endDate: computedDateRange.endDate,
                          });
                        }}
                      />
                      <div className="py-1">-</div>
                      <DateInput
                        value={computedDateRange.endDate}
                        onChange={(date) => {
                          handleTempRangeChange({
                            startDate: computedDateRange.startDate,
                            endDate: date,
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
                {isSmallScreen && (
                  <Select
                    value={computedPresetName || undefined}
                    onValueChange={(value) => {
                      setPreset(value as PresetName);
                    }}
                  >
                    <SelectTrigger className="mx-auto mb-2 w-45">
                      <SelectValue placeholder="Select preset..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PRESETS.map((preset) => (
                        <SelectItem key={preset.name} value={preset.name}>
                          {preset.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div>
                  <Calendar
                    mode="range"
                    onSelect={(value: { from?: Date; to?: Date } | undefined) => {
                      if (value?.from != null) {
                        handleTempRangeChange({
                          startDate: value.from.toJSON(),
                          endDate: value.to?.toISOString() ?? value.from.toISOString(),
                        });
                      }
                    }}
                    selected={{
                      from: new Date(computedDateRange.startDate),
                      to: new Date(computedDateRange.endDate),
                    }}
                    numberOfMonths={isSmallScreen ? 1 : 2}
                    defaultMonth={
                      computedDateRange.startDate
                        ? new Date(computedDateRange.startDate)
                        : new Date(
                            new Date().setMonth(new Date().getMonth() - (isSmallScreen ? 0 : 1)),
                          )
                    }
                  />
                </div>
              </div>
            </div>
            {!isSmallScreen && (
              <div className="flex flex-col items-end gap-1 pr-2 pb-6 pl-6">
                <div className="flex w-full flex-col items-end gap-1 pr-2 pb-6 pl-6">
                  {PRESETS.map((preset) => (
                    <PresetButton
                      key={preset.name}
                      preset={preset.name}
                      label={preset.label}
                      isSelected={isSelectedPreset(preset.name)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 py-2 pr-4">
            <Button onClick={handleCancel} variant="ghost">
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Update</Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

DateRangePicker.displayName = 'DateRangePicker';
DateRangePicker.filePath = 'libs/shared/ui-kit/src/lib/date-range-picker/date-range-picker.jsx';
